import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { supplementsAPI } from '../../services/api'

/**
 * VH-81: Purpose/benefits summary cell for the Daily Supplement Report.
 *
 * States:
 *   - Cached: render the summary text.
 *   - Empty (no cache or stale): render a clickable "Generate summary" CTA.
 *   - Generating: spinner + "Generating…" (CTA disabled).
 *   - Error: "—" + small "Retry" link.
 *
 * Generation is never triggered automatically — only via user click.
 */
export default function PurposeSummaryCell({ supplementId, initialSummary, language }) {
  const { t } = useTranslation()
  const [summary, setSummary] = useState(initialSummary || null)
  const [status, setStatus] = useState(initialSummary ? 'cached' : 'empty')
  const [error, setError] = useState(null)

  const handleGenerate = async () => {
    if (status === 'generating') return
    setStatus('generating')
    setError(null)
    try {
      const response = await supplementsAPI.generatePurposeSummary(supplementId, language)
      const newSummary = response.data?.summary || ''
      setSummary(newSummary)
      setStatus('cached')
    } catch (err) {
      console.warn(`[PurposeSummaryCell] Generation failed for supplement ${supplementId}:`, err)
      setError(err.message || 'Unknown error')
      setStatus('error')
    }
  }

  // Cached: show the summary
  if (status === 'cached' && summary) {
    return (
      <div className="text-sm text-gray-600 print:text-[8px] print:leading-tight print:whitespace-normal print:break-words">
        {summary}
      </div>
    )
  }

  // Generating: spinner + message
  if (status === 'generating') {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 print:hidden">
        <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>{t('report.generating')}</span>
      </div>
    )
  }

  // Error: show em dash + retry
  if (status === 'error') {
    return (
      <div className="flex items-center gap-2 print:hidden">
        <span className="text-gray-400">—</span>
        <button
          type="button"
          onClick={handleGenerate}
          className="text-xs text-indigo-600 hover:text-indigo-800 underline"
          title={error || ''}
        >
          {t('report.retry')}
        </button>
      </div>
    )
  }

  // Empty: click-to-generate CTA (hidden on print; print gets a blank cell)
  return (
    <button
      type="button"
      onClick={handleGenerate}
      className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 hover:underline print:hidden"
    >
      <SparklesIcon className="h-4 w-4" aria-hidden="true" />
      <span>{t('report.generateSummary')}</span>
    </button>
  )
}
